import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import {
  addCustomerPortalSupportInvolvedUser,
  createCustomerPortalSupportMessage,
  downloadCustomerPortalSupportAttachment,
  getCustomerPortalSupportInvolvedCandidates,
  getCustomerPortalSupportSla,
  getCustomerPortalSupportTicket,
  markCustomerPortalSupportViewed,
  uploadCustomerPortalSupportAttachment,
} from '../../api/customerPortalApi';
import { colorTokens, typographyTokens } from '../../app/designTokens';
import { useAuth } from '../../auth/AuthContext';
import { DetailField } from '../../components/InfoCard';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';
import { PageHeader } from '../../components/PageHeader';
import { SectionCard } from '../../components/SectionCard';
import { StatusBadge } from '../../components/StatusBadge';
import { SupportInterestedUsersAvatars } from '../../components/SupportInterestedUsersAvatars';
import { PortalSupportAddInvolvedDialog } from '../../components/PortalSupportAddInvolvedDialog';
import { Button } from '../../components/ui/Button';
import { IconButton } from '../../components/ui/IconButton';
import { TextInput } from '../../components/ui/TextInput';
import { useCustomerPortal } from '../../portal/CustomerPortalContext';
import { portalSupportWriteControls } from '../../utils/portalSupportCapabilities';

const { semantic } = colorTokens;

const wrapSx = {
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  whiteSpace: 'pre-wrap',
};

const monoSx = {
  fontFamily: typographyTokens.fontFamilyMono,
  fontSize: 13,
  lineHeight: '18px',
  color: semantic.text.primary,
};

const mutedSx = {
  fontSize: 13,
  lineHeight: '18px',
  color: semantic.text.muted,
};

const bodySx = {
  fontFamily: typographyTokens.fontFamily,
  fontSize: 13,
  lineHeight: '20px',
  color: semantic.text.primary,
  ...wrapSx,
};

const messageCardSx = {
  p: 2,
  borderRadius: 1,
  border: `1px solid ${semantic.border.subtle}`,
  bgcolor: semantic.bg.input,
};

function ticketCapabilities(ticket) {
  return ticket?.capabilities || {
    can_reply: false,
    can_upload_attachment: false,
    can_add_involved: false,
  };
}

export function CustomerPortalSupportTicketPage() {
  const { t } = useTranslation(['support', 'common', 'billing']);
  const { id } = useParams();
  useAuth();
  const { activeCustomerId } = useCustomerPortal();
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sla, setSla] = useState(null);
  const [involveOpen, setInvolveOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [addingInvolved, setAddingInvolved] = useState(false);

  const emptyValue = t('common:empty_value');
  const writeControls = portalSupportWriteControls(ticketCapabilities(ticket), ticket?.status);
  const {
    canReply,
    canUpload,
    canAddInvolved,
    showReopenHint,
    showClosedHint,
    showExpiredHint,
  } = writeControls;

  const formatDate = (value) => {
    if (!value) {
      return emptyValue;
    }

    return new Date(value).toLocaleString();
  };

  const formatSize = (bytes) => {
    if (!bytes && bytes !== 0) {
      return emptyValue;
    }
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const loadTicket = useCallback(() => {
    setLoading(true);
    setError('');

    return getCustomerPortalSupportTicket(id)
      .then((data) => {
        setTicket(data);
        markCustomerPortalSupportViewed(id).catch(() => {});
        getCustomerPortalSupportSla(id).then(setSla).catch(() => {});
        return data;
      })
      .catch(() => {
        setError(t('support:load_ticket_failed'));
        setTicket(null);
      })
      .finally(() => setLoading(false));
  }, [id, t]);

  useEffect(() => {
    loadTicket();
  }, [loadTicket]);

  const handleReply = async () => {
    setSubmitting(true);
    setError('');

    try {
      const updated = await createCustomerPortalSupportMessage(id, { body: reply });
      setReply('');
      if (updated?.id) {
        setTicket(updated);
      } else {
        await loadTicket();
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setError(t('support:portal.expired_readonly_hint'));
        await loadTicket();
      } else {
        setError(t('support:send_reply_failed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !activeCustomerId || !canUpload) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      await uploadCustomerPortalSupportAttachment(activeCustomerId, id, formData);
      await loadTicket();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setError(t('support:portal.expired_readonly_hint'));
        await loadTicket();
      } else {
        const message = err?.response?.data?.errors?.file?.[0] || err?.response?.data?.message;
        setError(message || t('support:attachments.uploadFailed'));
      }
    } finally {
      event.target.value = '';
    }
  };

  const handleDownload = async (attachment) => {
    if (!activeCustomerId) return;
    try {
      const blob = await downloadCustomerPortalSupportAttachment(activeCustomerId, id, attachment.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.original_filename || attachment.original_name || attachment.filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError(t('support:attachments.notAvailable'));
    }
  };

  const openInvolveDialog = async () => {
    if (!canAddInvolved) return;
    setInvolveOpen(true);
    setCandidatesLoading(true);
    setError('');
    try {
      const rows = await getCustomerPortalSupportInvolvedCandidates(id);
      setCandidates(Array.isArray(rows) ? rows : []);
    } catch (err) {
      if (err?.response?.status === 403) {
        setError(t('support:portal.expired_readonly_hint'));
        setInvolveOpen(false);
        await loadTicket();
      } else {
        setError(t('support:portal.add_involved_failed'));
        setInvolveOpen(false);
      }
    } finally {
      setCandidatesLoading(false);
    }
  };

  const handleAddInvolved = async (userId) => {
    setAddingInvolved(true);
    setError('');
    try {
      const result = await addCustomerPortalSupportInvolvedUser(id, userId);
      if (Array.isArray(result?.involved_users)) {
        setTicket((prev) => (prev ? { ...prev, involved_users: result.involved_users } : prev));
      } else {
        await loadTicket();
      }
      setInvolveOpen(false);
      setCandidates([]);
    } catch {
      setError(t('support:portal.add_involved_failed'));
      await loadTicket();
      setInvolveOpen(false);
    } finally {
      setAddingInvolved(false);
    }
  };

  if (loading) {
    return <LoadingSkeleton type="Detail" />;
  }

  if (!ticket) {
    return (
      <SectionCard variant="Danger" sx={{ mb: 0 }}>
        <Alert severity="error" sx={{ bgcolor: 'transparent', p: 0 }}>
          {error || t('support:ticket_not_found_short')}
        </Alert>
        <Box sx={{ mt: 2 }}>
          <Button variant="Ghost" onClick={loadTicket}>
            {t('common:retry')}
          </Button>
        </Box>
      </SectionCard>
    );
  }

  const messages = ticket.messages || ticket.comments || [];
  const attachments = ticket.attachments || ticket.files || [];
  const title = ticket.theme || ticket.subject || ticket.form_type || t('support:ticket_detail');
  const formFields = ticket.form_fields || [];
  const readonlyHint = showClosedHint
    ? t('support:portal.closed_readonly_hint')
    : (showExpiredHint ? t('support:portal.expired_readonly_hint') : null);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="Detail"
        title={title}
        subtitle={ticket.public_number || undefined}
        backTo="/portal/support"
        badge={(
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {ticket.status ? (
              <StatusBadge status={ticket.status} category="support" size="Md" />
            ) : null}
            {ticket.priority ? (
              <StatusBadge status={ticket.priority} category="priority" size="Md" />
            ) : null}
          </Stack>
        )}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {readonlyHint ? (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="portal-support-readonly-hint">
          {readonlyHint}
        </Alert>
      ) : null}

      <SectionCard title={t('support:metadata')} sx={{ mb: 2.5 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2.5,
          }}
        >
          <DetailField label={t('billing:number')}>
            <Typography sx={monoSx}>{ticket.public_number || emptyValue}</Typography>
          </DetailField>
          <DetailField label={t('support:form.ticket_type')}>
            <Typography sx={{ ...bodySx, whiteSpace: 'normal' }}>
              {ticket.form_type || ticket.type || emptyValue}
            </Typography>
          </DetailField>
          <DetailField label={t('common:status')}>
            {ticket.status
              ? <StatusBadge status={ticket.status} category="support" size="Sm" />
              : emptyValue}
          </DetailField>
          <DetailField label={t('support:tickets.priority')}>
            {ticket.priority
              ? <StatusBadge status={ticket.priority} category="priority" size="Sm" />
              : emptyValue}
          </DetailField>
          {sla ? (
            <>
              <DetailField
                label={t('support:first_response_due')}
                value={formatDate(sla.first_response_due_at_local || sla.first_response_due_at)}
              />
              <DetailField
                label={t('support:first_response_policy_label')}
                value={t('support:first_response_policy_help')}
              />
            </>
          ) : null}
          <DetailField label={t('support:interested_users')}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <SupportInterestedUsersAvatars
                users={ticket.involved_users || []}
                emptyLabel={t('support:no_interested_users')}
              />
              {canAddInvolved ? (
                <IconButton
                  size="Sm"
                  tone="Neutral"
                  aria-label={t('support:add_interested_user')}
                  onClick={openInvolveDialog}
                  data-testid="portal-support-add-involved"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Stack>
          </DetailField>
        </Box>
      </SectionCard>

      <SectionCard title={t('support:form.form_data')} sx={{ mb: 2.5 }}>
        {formFields.length === 0 ? (
          ticket.description ? (
            <Typography sx={bodySx}>{ticket.description}</Typography>
          ) : (
            <Typography sx={mutedSx}>{emptyValue}</Typography>
          )
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2.5,
            }}
          >
            {formFields.map((field) => (
              <DetailField key={field.key} label={field.label}>
                <Typography sx={bodySx}>{field.value || emptyValue}</Typography>
              </DetailField>
            ))}
          </Box>
        )}
      </SectionCard>

      <SectionCard
        title={t('support:attachments.title')}
        actions={canUpload ? (
          <Button variant="Secondary" component="label" sx={{ minHeight: 32 }} data-testid="portal-support-upload">
            {t('support:attachments.add')}
            <input type="file" hidden onChange={handleUpload} />
          </Button>
        ) : null}
        sx={{ mb: 2.5 }}
      >
        {attachments.length === 0 ? (
          <Typography sx={mutedSx}>{t('support:no_files')}</Typography>
        ) : (
          <Stack spacing={1.5}>
            {attachments.map((file) => (
              <Stack
                key={file.id}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                sx={{
                  py: 1,
                  borderBottom: `1px solid ${semantic.border.subtle}`,
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ ...bodySx, whiteSpace: 'normal' }}>
                    {file.original_filename || file.original_name || file.filename}
                  </Typography>
                  <Typography sx={mutedSx}>{formatSize(file.size_bytes)}</Typography>
                </Box>
                <Button
                  variant="Ghost"
                  onClick={() => handleDownload(file)}
                  sx={{ minHeight: 32 }}
                >
                  {t('support:attachments.download')}
                </Button>
              </Stack>
            ))}
          </Stack>
        )}
      </SectionCard>

      <SectionCard title={t('support:conversation')} sx={{ mb: 2.5 }}>
        {messages.length === 0 ? (
          <Typography sx={mutedSx}>{t('support:no_public_messages')}</Typography>
        ) : (
          <Stack spacing={1.5}>
            {messages.map((message) => (
              <Box key={message.id} sx={messageCardSx}>
                <Typography
                  sx={{
                    fontSize: 12,
                    lineHeight: '16px',
                    fontWeight: typographyTokens.fontWeightSemibold,
                    color: semantic.text.secondary,
                    mb: 1,
                  }}
                >
                  {message.author_name || t('support:user')}
                  {' · '}
                  {formatDate(message.created_at)}
                </Typography>
                <Typography sx={bodySx}>{message.body}</Typography>
                {(message.attachments || []).length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                    {(message.attachments || []).map((file) => (
                      <Button
                        key={file.id}
                        variant="Ghost"
                        onClick={() => handleDownload(file)}
                        sx={{ minHeight: 32 }}
                      >
                        {file.original_filename || file.original_name || file.filename}
                      </Button>
                    ))}
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </SectionCard>

      {canReply ? (
        <SectionCard title={t('support:tickets.reply')} sx={{ mb: 0 }} data-testid="portal-support-reply">
          {showReopenHint ? (
            <Alert severity="info" sx={{ mb: 2 }} data-testid="portal-support-reopen-hint">
              {t('support:portal.reply_reopens_hint')}
            </Alert>
          ) : null}
          <TextInput
            density="Normal"
            label={t('support:your_message')}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            multiline
            minRows={4}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
            <Button
              variant="Accent"
              onClick={handleReply}
              loading={submitting}
              disabled={submitting || !reply.trim()}
            >
              {submitting ? t('support:sending') : t('support:send_reply')}
            </Button>
            {canUpload ? (
              <Button variant="Secondary" component="label">
                {t('support:attachments.add')}
                <input type="file" hidden onChange={handleUpload} />
              </Button>
            ) : null}
          </Stack>
        </SectionCard>
      ) : null}

      <PortalSupportAddInvolvedDialog
        open={involveOpen}
        onClose={() => setInvolveOpen(false)}
        candidates={candidates}
        loading={candidatesLoading}
        submitting={addingInvolved}
        title={t('support:portal.add_involved_title')}
        emptyLabel={t('support:portal.no_candidates')}
        cancelLabel={t('common:cancel')}
        loadingLabel={t('common:loading')}
        onSelect={handleAddInvolved}
      />
    </Box>
  );
}
